import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Features',     href: '/features' },
  { label: 'Pricing',      href: '/pricing' },
  { label: 'Integrations', href: '/integrations' },
  { label: 'Solutions',    href: '/solutions' },
  { label: 'Blog',         href: '/blog' },
];

const NAV_SECONDARY = [
  { label: 'About',    href: '/about' },
  { label: 'Contact',  href: '/contact' },
  { label: 'Docs',     href: '/docs' },
  { label: 'Changelog', href: '/changelog' },
];

export default function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [announced, setAnnounced] = useState(
    () => !sessionStorage.getItem('zepliq_ann_dismissed')
  );
  const location = useLocation();

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--navbar-h', announced ? '112px' : '72px'
    );
  }, [announced]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const dismiss = () => {
    sessionStorage.setItem('zepliq_ann_dismissed', '1');
    setAnnounced(false);
  };

  return (
    <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${menuOpen ? 'navbar--menu-open' : ''}`} role="banner">

      {/* Announcement Bar */}
      {announced && (
        <div className="navbar__ann">
          <div className="navbar__ann-inner">
            <span className="navbar__ann-pill">New</span>
            <span className="navbar__ann-text">
              Shopify &amp; WooCommerce order confirmation is live
            </span>
            <Link to="/features" className="navbar__ann-link">
              See what's new
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
          <button className="navbar__ann-close" onClick={dismiss} aria-label="Dismiss">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      )}

      {/* Main Nav */}
      <nav className="navbar__inner container" aria-label="Main navigation">

        <Link to="/" className="navbar__logo" aria-label="Zepliq home">
          <img src="/logo.svg" alt="Zepliq logo" className="navbar__logo-img" />
          <span className="navbar__logo-text">Zepliq</span>
        </Link>

        <ul className="navbar__links">
          {NAV_LINKS.map(link => (
            <li key={link.href}>
              <Link
                to={link.href}
                className={`navbar__link ${location.pathname === link.href ? 'navbar__link--active' : ''}`}
              >
                {link.label}
                <span className="navbar__link-line" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>

        <div className="navbar__actions">
          <a href="https://dashboard.zepliq.com" className="navbar__login">Log In</a>

          <Link to="/contact" className="navbar__cta">
            Start Free
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
          <button
            className={`navbar__hamburger ${menuOpen ? 'navbar__hamburger--open' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer — full-screen, own header row */}
      <div className={`navbar__drawer ${menuOpen ? 'navbar__drawer--open' : ''}`} role="dialog" aria-modal="true" aria-label="Navigation menu">
        {/* Drawer header: logo + close button */}
        <div className="navbar__drawer-header">
          <Link to="/" className="navbar__logo" onClick={() => setMenuOpen(false)} aria-label="Zepliq home">
            <img src="/logo.svg" alt="Zepliq logo" className="navbar__logo-img" />
            <span className="navbar__logo-text">Zepliq</span>
          </Link>
          <button className="navbar__drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Primary nav links */}
        <ul className="navbar__drawer-links">
          {NAV_LINKS.map(link => (
            <li key={link.href}>
              <Link
                to={link.href}
                className={`navbar__drawer-link ${location.pathname === link.href ? 'navbar__drawer-link--active' : ''}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Secondary links */}
        <div className="navbar__drawer-secondary">
          <span className="navbar__drawer-section-label">Company</span>
          <ul className="navbar__drawer-links navbar__drawer-links--secondary">
            {NAV_SECONDARY.map(link => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className={`navbar__drawer-link navbar__drawer-link--sm ${location.pathname === link.href ? 'navbar__drawer-link--active' : ''}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA buttons pinned to bottom */}
        <div className="navbar__drawer-actions">
          <a href="https://dashboard.zepliq.com" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>Log In</a>
          <Link to="/contact" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Start Free →</Link>
        </div>
      </div>

    </header>
  );
}
